/**
 * Network-error interceptor — distinguishes real CORS bugs from
 * edge/proxy failures that LOOK like CORS bugs.
 *
 * Why
 * ---
 * When Cloudflare returns a 520/521/522/523/524 or any layer between the
 * browser and FastAPI strips the response, the browser logs the request
 * as "CORS error" because no `Access-Control-Allow-Origin` header came
 * back — even though the actual failure is upstream connectivity, not
 * CORS misconfig. Past debugging sessions wasted hours chasing CORS
 * tickets that turned out to be Cloudflare-for-SaaS origin mismatches.
 *
 * What this does
 * --------------
 * 1. Installs a single axios response interceptor on the default
 *    instance. Every page in the codebase does `import axios from "axios"`
 *    and gets the default instance, so one install covers all of them.
 * 2. On every error, emits a tagged console group with the full failure
 *    fingerprint (URL, method, browser-side error code, response status
 *    if any, response headers if any). Tag prefix: `[NET-DEBUG]`.
 * 3. Classifies the failure as one of:
 *      - "edge_5xx"         — Cloudflare/proxy returned 520-524 (NOT CORS).
 *      - "no_response"      — request never got a response (network down,
 *                              DNS, or the browser blocked the request
 *                              because the preflight failed).
 *      - "real_cors"        — backend reachable but missing/wrong CORS
 *                              headers (true CORS bug).
 *      - "http_error"       — backend returned 4xx/5xx with a body. Normal
 *                              app error, not a network problem.
 *    The classification is stamped onto the error object as
 *    `error.networkClassification` so callers can branch on it.
 * 4. Shows a one-line user-visible toast for `edge_5xx` and `no_response`
 *    so a Cloudflare outage doesn't look like a login bug. `real_cors`
 *    and `http_error` are passed through silently — the app's existing
 *    error handlers deal with them.
 *
 * How to triage from DevTools (workflow doc)
 * ------------------------------------------
 *   1. Open Console — look for `[NET-DEBUG]` group with classification.
 *   2. If classification == "edge_5xx" → fix Cloudflare/Emergent
 *      custom-domain routing. Do NOT change CORS code.
 *   3. If classification == "real_cors" → hit `GET /api/_cors-debug`
 *      from the failing browser to see what the backend received.
 *   4. If classification == "no_response" with no preceding edge_5xx →
 *      check user's network, then check the deployed app's liveness.
 */

import axios from "axios";
import { toast } from "sonner";

// Cloudflare's "origin connectivity" status codes. 520 is the most common
// (the upstream returned an unknown error / closed the connection); 521-524
// are 'origin down', 'origin SSL handshake failed', 'invalid SSL cert',
// 'origin took too long'. All four mean "fix the edge", not "fix CORS".
const CLOUDFLARE_EDGE_STATUSES = new Set([520, 521, 522, 523, 524]);

// ── Transient-retry policy (GET/HEAD only) ──────────────────────────────────
// Only idempotent reads are ever retried, so a brief DB/edge blip self-heals
// instead of surfacing a hard error (see billing onboarding RCA). Mutations
// (POST/PUT/PATCH/DELETE) are NEVER retried — this is a Stripe + legal-records
// app, and a retried payment/record/email would be far worse than an error.
// Opt a specific GET out with `axios.get(url, { __noRetry: true })`.
const IDEMPOTENT_METHODS = new Set(["get", "head"]);
// Server/edge statuses that are transient and safe to re-attempt. Note 500/501
// are intentionally excluded (likely a deterministic app bug — retrying just
// adds latency); our transient MongoDB errors are mapped to 503 server-side.
const RETRYABLE_STATUSES = new Set([429, 502, 503, 504, 520, 521, 522, 523, 524]);
const MAX_RETRIES = 2; // up to 3 total attempts

const _sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export function canRetry(error, cfg) {
  const method = (cfg.method || "get").toLowerCase();
  if (!IDEMPOTENT_METHODS.has(method)) return false;
  if (cfg.__noRetry) return false;
  if ((cfg.__retryCount || 0) >= MAX_RETRIES) return false;
  if (axios.isCancel && axios.isCancel(error)) return false;
  if (error.code === "ERR_CANCELED") return false;
  // No response at all (network/timeout) is retryable; otherwise gate on status.
  if (!error.response) return true;
  return RETRYABLE_STATUSES.has(error.response.status);
}

export function retryDelay(error, attempt) {
  // Honor an explicit Retry-After (seconds) from the server (we send it on 503).
  const ra = error.response && error.response.headers && error.response.headers["retry-after"];
  if (ra) {
    const secs = parseInt(ra, 10);
    if (!Number.isNaN(secs)) return Math.min(secs * 1000, 5000);
  }
  // Otherwise exponential backoff with jitter: ~0.4s, ~0.8s.
  const base = 400 * Math.pow(2, attempt - 1);
  return Math.min(base + Math.random() * 200, 3000);
}


let _installed = false;
let _toastSuppressUntil = 0;

function classify(error) {
  // Network-level error (no response received at all).
  if (!error.response) {
    return "no_response";
  }
  const status = error.response.status;
  if (CLOUDFLARE_EDGE_STATUSES.has(status)) {
    return "edge_5xx";
  }
  // Response received but the BROWSER may have blocked it for missing CORS
  // headers. axios can't see that directly (the browser surfaces it as a
  // "no_response" with `code: "ERR_NETWORK"`), so "real_cors" is really
  // diagnosed by the user from the console message + DevTools Network tab.
  // We still flag 4xx/5xx so callers can see the bucket.
  if (status >= 400) {
    return "http_error";
  }
  return "ok";
}

function fingerprint(error) {
  const cfg = error.config || {};
  const res = error.response;
  const baseUrl = (cfg.baseURL || "").replace(/\/$/, "");
  const url = cfg.url ? `${baseUrl}${cfg.url}` : "<no-url>";
  const out = {
    method: (cfg.method || "GET").toUpperCase(),
    url,
    browser_origin: typeof window !== "undefined" ? window.location.origin : null,
    axios_code: error.code || null,
    axios_message: error.message || null,
  };
  if (res) {
    out.status = res.status;
    out.status_text = res.statusText;
    out.response_headers = res.headers || null;
    out.cf_ray = (res.headers && (res.headers["cf-ray"] || res.headers["CF-RAY"])) || null;
    out.has_acao = !!(res.headers && res.headers["access-control-allow-origin"]);
  }
  return out;
}

function showToastOnce(message) {
  // Rate-limit: at most one network-failure toast every 8 seconds, so a
  // burst of failed XHRs from a single dead endpoint doesn't paper the
  // screen.
  const now = Date.now();
  if (now < _toastSuppressUntil) return;
  _toastSuppressUntil = now + 8000;
  toast.error(message, { duration: 6000 });
}

export function installNetworkErrorInterceptor() {
  if (_installed) return;
  _installed = true;

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const cfg = error.config || {};

      // ── Transient-retry (GET/HEAD only) BEFORE any logging/toast ──────────
      // so a recovered blip is invisible and doesn't spam the console/toasts.
      if (canRetry(error, cfg)) {
        cfg.__retryCount = (cfg.__retryCount || 0) + 1;
        await _sleep(retryDelay(error, cfg.__retryCount));
        return axios(cfg); // re-issue through the full interceptor chain
      }

      const classification = classify(error);
      error.networkClassification = classification;

      // ── Session-expiry (401) backstop ───────────────────────────────────
      // A 401 on an authenticated /api call means the stored token is expired
      // or invalid. Surface it to the user (SessionExpiryManager) instead of
      // letting the page silently fail to save/load. Fire ONLY when a token is
      // actually present (otherwise it's a normal unauthenticated request that
      // ProtectedRoute handles), never for the auth endpoints themselves, and
      // never for requests that opt out via `__noSessionPrompt` (e.g. renew).
      try {
        if (
          error.response &&
          error.response.status === 401 &&
          !cfg.__noSessionPrompt &&
          typeof window !== "undefined" &&
          window.localStorage &&
          localStorage.getItem("compass_token")
        ) {
          const url = `${cfg.baseURL || ""}${cfg.url || ""}`;
          const isAuthEndpoint =
            /\/api\/auth\/(login|register|renew|me|request-otp|verify-otp|google|magic)/i.test(url);
          if (!isAuthEndpoint) {
            window.dispatchEvent(new CustomEvent("compass:session-lost"));
          }
        }
      } catch (_e) {
        /* never let session detection break the error path */
      }

      const fp = fingerprint(error);

      // Tagged console group so it's trivially greppable in DevTools and
      // distinct from app-level errors.
      // eslint-disable-next-line no-console
      console.groupCollapsed(
        `%c[NET-DEBUG] ${classification} — ${fp.method} ${fp.url}`,
        "color:#b91c1c;font-weight:bold;",
      );
      // eslint-disable-next-line no-console
      console.log("classification:", classification);
      // eslint-disable-next-line no-console
      console.log("fingerprint:", fp);
      if (classification === "edge_5xx") {
        // eslint-disable-next-line no-console
        console.log(
          "%cThis is NOT a CORS bug. Cloudflare/edge returned a 5xx without " +
            "CORS headers, so the browser logs it as 'CORS error', but the " +
            "real failure is upstream. Action: repoint the Cloudflare " +
            "Custom Hostname origin to the current Emergent deployment.",
          "color:#b45309;",
        );
        if (fp.cf_ray) {
          // eslint-disable-next-line no-console
          console.log("CF-Ray (give this to Emergent Support):", fp.cf_ray);
        }
      } else if (classification === "no_response") {
        // eslint-disable-next-line no-console
        console.log(
          "%cNo response received. Most often: (a) Cloudflare 520 on a " +
            "preflight (look for a sibling row with status 520 in Network), " +
            "(b) DNS/network failure, or (c) the browser blocked the request " +
            "for a real CORS violation. Check the Network tab for a " +
            "preflight (OPTIONS) row.",
          "color:#b45309;",
        );
      }
      // eslint-disable-next-line no-console
      console.groupEnd();

      // User-visible toast for failures that aren't normal app errors.
      if (classification === "edge_5xx") {
        showToastOnce(
          "Couldn't reach the server. This looks like a network/proxy " +
            "outage, not a login problem. Please try again in a minute.",
        );
      } else if (classification === "no_response") {
        showToastOnce(
          "Couldn't reach the server. Check your connection — if others " +
            "see the same thing, the site's edge is down.",
        );
      }

      return Promise.reject(error);
    },
  );
}

// Expose a tiny window helper so anyone can confirm the interceptor is
// active from DevTools: `window.__netDebug.classification` after a failure
// will show the last classification.
if (typeof window !== "undefined") {
  window.__netDebug = window.__netDebug || {};
}
